<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :style="{ width: '450px' }"
    :header="dialogHeader"
  >
    <div class="p-fluid">
      <div
        class="field"
        style="text-align: left;"
      >
        <label for="newListName">{{ t('myLists.actions.create.listNameLabel') }}</label>
        <InputText
          id="newListName"
          v-model="listName"
          :placeholder="t('myLists.actions.create.listNamePlaceholder')"
          :class="{ 'p-invalid': v$.listName.$error }"
          @blur="v$.listName.$touch()"
        />
        <small
          v-if="v$.listName.$error"
          class="p-error"
        >
          {{ getErrorMessage() }}
        </small>
      </div>
    </div>
    <template #footer>
      <SpmButton
        :label="t('common.cancel')"
        icon="pi pi-times"
        class-style="p-button p-button-secondary"
        @click="close"
      />
      <SpmButton
        :label="t('validate')"
        icon="pi pi-check"
        class-style="p-button p-button-success"
        :disabled="!listName.trim() || v$.listName.$error"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import {
  required,
  minLength,
  maxLength,
  helpers,
} from '@vuelidate/validators';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import type { ActionPayload, CreateListResult } from './types';

// Custom validator for alphanumeric with spaces
const alphanumericWithSpaces = helpers.regex(/^[a-zA-Z0-9\s]+$/);

export default defineComponent({
  name: 'CreateListAction',

  components: {
    Dialog,
    InputText,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();

    // Internal state
    const isVisible = ref(false);
    const listName = ref('');
    const currentPayload = ref<ActionPayload | null>(null);

    // Validation rules
    const rules = computed(() => ({
      listName: {
        required: helpers.withMessage(
          () => t('myLists.actions.create.validation.required'),
          required,
        ),
        minLength: helpers.withMessage(
          () => t('myLists.actions.create.validation.minLength', { min: 3 }),
          minLength(3),
        ),
        maxLength: helpers.withMessage(
          () => t('myLists.actions.create.validation.maxLength', { max: 30 }),
          maxLength(30),
        ),
        alphanumericWithSpaces: helpers.withMessage(
          () => t('myLists.actions.create.validation.alphanumeric'),
          alphanumericWithSpaces,
        ),
      },
    }));

    const v$ = useVuelidate(rules, { listName });

    // Computed header based on context
    const dialogHeader = computed(() => {
      if (currentPayload.value?.context === 'grouped') {
        return t('myLists.actions.create.headerGrouped');
      }
      return t('myLists.actions.create.headerRow');
    });

    /**
     * Get the first error message for listName
     */
    const getErrorMessage = () => {
      if (v$.value.listName.$errors.length > 0) {
        return v$.value.listName.$errors[0].$message;
      }
      return '';
    };

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: ActionPayload) => {
      currentPayload.value = payload;
      listName.value = '';
      v$.value.$reset();
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentPayload.value = null;
      v$.value.$reset();
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = async () => {
      if (!currentPayload.value) return;

      // Validate before confirming
      const isValid = await v$.value.$validate();
      if (!isValid) return;

      const result: CreateListResult = {
        context: currentPayload.value.context,
        listName: listName.value.trim(),
        selectionInfo: currentPayload.value.selectionInfo,
        rowId: currentPayload.value.rowId,
      };

      emit('confirm', result);
      close();
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      listName,
      dialogHeader,
      close,
      confirm,
      v$,
      getErrorMessage,
    };
  },
});
</script>
